/**
 * Montagem do PDF dos relatórios gerenciais.
 *
 * ## Por que está separado da tela
 *
 * `montarPdf` devolve os bytes; quem baixa é a tela. A divisão existe porque a montagem é a parte que
 * pode falhar de verdade — e falhar em produção, no clique do usuário, sem nada no console de quem
 * desenvolve. Assim ela roda em teste, no Node, sem navegador.
 *
 * ## A armadilha do jspdf-autotable
 *
 * `jspdf-autotable@5` publica tipos que **não declaram** export default, mas o bundle ESM exporta
 * `autoTable as default`. O resultado é `tsc` reprovando `autoTable(doc, ...)` com "expression is not
 * callable" enquanto o código funciona. É um dos dois erros conhecidos de `tsc -p apps/painel`.
 *
 * Em vez de silenciar com `as any`, a importação lê o export **nomeado**, que os tipos declaram e o
 * bundle também exporta. Some o erro de tipo e some a dependência de um default que a próxima versão
 * pode remover — foi assim que a v5 quebrou quem usava default.
 */

import type { DadosRelatorio } from './relatorios.ts';

/**
 * Bytes do PDF. A tela decide o que fazer com eles.
 *
 * Devolve `ArrayBuffer`, e não `Uint8Array`, porque é o que o `Blob` aceita direto: a view genérica
 * `Uint8Array<ArrayBufferLike>` não satisfaz `BlobPart`, já que `ArrayBufferLike` admite
 * `SharedArrayBuffer`. Evita uma conversão no ponto de download e o `as` que ela pediria.
 */
export async function montarPdf(
  dados: DadosRelatorio,
  descricaoPeriodo: string,
  geradoEm: Date,
): Promise<ArrayBuffer> {
  const { jsPDF } = await import('jspdf');
  const { autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(dados.titulo, 14, 20);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Período: ${descricaoPeriodo}`, 14, 28);
  doc.text(`Gerado em: ${geradoEm.toLocaleString('pt-BR')}`, 14, 34);

  autoTable(doc, {
    startY: 40,
    head: [[...dados.cabecalho]],
    body: dados.linhas.map((l) => [...l.colunas]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [10, 37, 64], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  if (dados.resumo !== undefined) {
    /*
     * `lastAutoTable` é escrito no documento pelo plugin, e os tipos do jsPDF não o conhecem. A
     * asserção é estreita de propósito, e tem um piso: se o plugin deixar de publicar a propriedade, o
     * resumo vai para uma posição fixa em vez de estourar em `undefined.finalY` e perder o PDF inteiro.
     */
    const comTabela = doc as unknown as { lastAutoTable?: { finalY?: number } };
    const finalY = comTabela.lastAutoTable?.finalY ?? 40 + dados.linhas.length * 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(dados.resumo, 14, finalY + 10);
  }

  return doc.output('arraybuffer');
}
